/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { animated, useSpring } from '@react-spring/web';
import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';

import { ProductCategory } from '@toucan/types/interfaces';

import { CategoryListSection } from './category-list-section';
import { styles } from './category-list.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CategoryListProps {
    /**
     * The category to be displayed in the CategoryList
     */
    category: ProductCategory;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

export const CategoryList: FunctionComponent<CategoryListProps> = ({
    category,
    'data-testid': testId = 'category-list__category',
    ...otherProps
}: CategoryListProps) => {
    const [showSubcategory, setShowSubcategory] = useState<boolean>(false);
    const [activeSubcategory, setActiveSubcategory] =
        useState<ProductCategory | null>(null);

    /**
     * Handle a clicked category event
     *
     * @param {ProductCategory} category - Information about the clicked category
     * @returns {void}
     */
    function onCategoryClick(category: ProductCategory) {
        setActiveSubcategory(category);
        setShowSubcategory(true);
    }

    /**
     * Handle a clicked back button event
     * @returns {void}
     */
    function onBackClick() {
        setShowSubcategory(false);
    }

    const props = useSpring({
        from: {
            transform: 'translate3d(-100%, 0, 0)',
        },
        to: {
            transform: showSubcategory
                ? 'translate3d(0, 0, 0)'
                : 'translate3d(-100%, 0, 0)',
        },
    });

    // leave this here or it breaks the sub category menu
    const StyledCategoryList = styled('div').withConfig({
        shouldForwardProp: (prop) => isPropValid(prop),
    })`
        ${styles}
    `;

    return (
        <StyledCategoryList category={category}>
            <CategoryListSection
                category={category}
                onCategoryClick={onCategoryClick}
                data-testid={testId}
                {...otherProps}
            />
            <animated.div style={props} className="subcategory">
                {activeSubcategory && (
                    <CategoryListSection
                        category={activeSubcategory}
                        onCategoryClick={onCategoryClick}
                        onBackClick={onBackClick}
                        isSubcategory={true}
                        data-testid={`${testId}__child`}
                        {...otherProps}
                    />
                )}
            </animated.div>
        </StyledCategoryList>
    );
};
